/**
 * Loader/Spinner Component
 * Reusable loading indicator for the application
 */

'use client';

export interface LoaderProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  color?: string;
  fullScreen?: boolean;
  message?: string;
  variant?: 'spinner' | 'dots' | 'pulse';
}

const sizeMap = {
  sm: '16px',
  md: '32px',
  lg: '48px',
  xl: '64px',
};

export function Loader({ size = 'md', color = '#0070f3', fullScreen = false, message, variant = 'spinner' }: LoaderProps) {
  const loaderSize = sizeMap[size];

  const loaderContent = (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '1rem',
      }}
    >
      {variant === 'spinner' && <SpinnerLoader size={loaderSize} color={color} />}
      {variant === 'dots' && <DotsLoader size={loaderSize} color={color} />}
      {variant === 'pulse' && <PulseLoader size={loaderSize} color={color} />}

      {/* {message && (
                <p style={{
                    margin: 0,
                    color: color,
                    fontSize: '0.875rem',
                    fontWeight: 500,
                }}>
                    {message}
                </p>
            )} */}
    </div>
  );

  if (fullScreen) {
    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          zIndex: 9999,
        }}
      >
        {loaderContent}
      </div>
    );
  }

  return loaderContent;
}

function SpinnerLoader({ size, color }: { size: string; color: string }) {
  return (
    <div
      style={{
        width: size,
        height: size,
        border: `3px solid rgba(0, 0, 0, 0.1)`,
        borderTopColor: color,
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
      }}
    >
      <style>{`
        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </div>
  );
}

function DotsLoader({ size, color }: { size: string; color: string }) {
  const dotSize = `calc(${size} / 4)`;

  return (
    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          style={{
            width: dotSize,
            height: dotSize,
            backgroundColor: color,
            borderRadius: '50%',
            animation: `bounce 1.4s ease-in-out ${i * 0.16}s infinite`,
          }}
        />
      ))}
      <style>{`
        @keyframes bounce {
          0%,
          80%,
          100% {
            transform: scale(0);
          }
          40% {
            transform: scale(1);
          }
        }
      `}</style>
    </div>
  );
}

function PulseLoader({ size, color }: { size: string; color: string }) {
  return (
    <div
      style={{
        width: size,
        height: size,
        backgroundColor: color,
        borderRadius: '50%',
        animation: 'pulse 1.5s ease-in-out infinite',
      }}
    >
      <style>{`
        @keyframes pulse {
          0%,
          100% {
            opacity: 1;
            transform: scale(0.8);
          }
          50% {
            opacity: 0.5;
            transform: scale(1);
          }
        }
      `}</style>
    </div>
  );
}

/**
 * Inline loader for buttons and small spaces
 */
export function InlineLoader({ size = 'sm', color = 'currentColor' }: Pick<LoaderProps, 'size' | 'color'>) {
  return (
    <div style={{ display: 'flex', height: '100vh', alignItems: 'center' }}>
      <Loader size={size} color={color} variant="spinner" />
    </div>
  );
}
